import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chapter } from '@/types';

interface UseChapterVisibilityArgs {
  bookId?: string;
  chapters: Chapter[];
  initialChapterSeq: number;
}

interface UseChapterVisibilityResult {
  visibleChapter: number;
  setVisibleChapter: (sequence: number) => void;
  setChapterRef: (el: HTMLElement | null, chapterId: string) => void;
}

export function useChapterVisibility({
  bookId,
  chapters,
  initialChapterSeq,
}: UseChapterVisibilityArgs): UseChapterVisibilityResult {
  const navigate = useNavigate();
  const chapterRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [visibleChapter, setVisibleChapter] = useState(initialChapterSeq);

  const setChapterRef = useCallback((el: HTMLElement | null, chapterId: string) => {
    if (el) chapterRefs.current.set(chapterId, el);
  }, []);

  useEffect(() => {
    if (chapters.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const seq = parseInt(entry.target.getAttribute('data-chapter') || '1');
            if (seq !== visibleChapter) {
              setVisibleChapter(seq);
              if (bookId) {
                navigate(`/book/${bookId}/${seq}`, { replace: true });
              }
            }
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );

    chapterRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [bookId, chapters, navigate, visibleChapter]);

  return {
    visibleChapter,
    setVisibleChapter,
    setChapterRef,
  };
}
